import dayjs from 'dayjs';

import {
  JVMContainerMetrics,
  OptimizationWorkloadContainerMetrics,
} from '@cast/types';

import {
  CpuChartDataPoint,
  CpuPredictionDataPoint,
  JvmChartDataPoint,
  JvmMetricKey,
  MemoryChartDataPoint,
} from './_constants';

export const prepareCpuHistoricalChartMetrics = (
  metrics:
    | (OptimizationWorkloadContainerMetrics & { avgStall?: number })[]
    | undefined
): CpuChartDataPoint[] => {
  if (!metrics) return [];

  return metrics.map((m) => ({
    timestamp: m.timestamp,
    max: m.max,
    min: m.min,
    p50: m.p50,
    p75: m.p75,
    rec: m.rec,
    req: m.req,
    avgStall: m.avgStall ?? null,
  }));
};

export const prepareCpuPredictionChartMetrics = (
  metrics: OptimizationWorkloadContainerMetrics[] | undefined
): CpuPredictionDataPoint[] => {
  if (!metrics) return [];

  const now = dayjs().utc();
  return metrics.map((m) => ({
    timestamp: m.timestamp,
    rec: dayjs.utc(m.timestamp).isAfter(now) ? null : m.rec,
    avg: dayjs.utc(m.timestamp).isAfter(now) ? null : m.avg,
    recPredicted: m.recPredicted,
    avgPredicted: m.avgPredicted,
  }));
};

export const prepareMemoryChartMetrics = (
  metrics: OptimizationWorkloadContainerMetrics[] | undefined
): MemoryChartDataPoint[] => {
  if (!metrics) return [];

  return metrics.map((m) => ({
    timestamp: m.timestamp,
    max: m.max,
    min: m.min,
    p50: m.p50,
    p75: m.p75,
    rec: m.rec,
    req: m.req,
  }));
};

export const prepareJvmChartData = (
  jvm: JVMContainerMetrics | undefined
): JvmChartDataPoint[] => {
  const heapUsedMetrics = jvm?.heapUsed?.metricsGib;
  if (!heapUsedMetrics) return [];

  // BE aligns data, it's safe to group by index
  return heapUsedMetrics.map(({ timestamp }, i) => ({
    timestamp,
    heapUsed: heapUsedMetrics[i]?.p95 ?? null,
    heapCommitted: jvm?.heapCommitted?.metricsGib?.[i]?.avg ?? null,
    heapRecommended: jvm?.recommendedHeap?.metricsGib?.[i]?.avg ?? null,
  }));
};

export const prepareJvmMetrics = (
  jvm: JVMContainerMetrics | undefined
): Record<JvmMetricKey, number | undefined> => {
  return {
    heapUsed: jvm?.heapUsed?.aggregatedGib?.p95,
    heapCommitted: jvm?.heapCommitted?.aggregatedGib?.avg,
    heapRecommended: jvm?.recommendedHeap?.aggregatedGib?.avg,
  };
};
